import { HttpClient, HttpParams } from '@angular/common/http';
import { forkJoin, of, zip } from 'rxjs';
import { map, concatMap } from 'rxjs/operators';
import { RecordType } from 'modules/shared/models/record.models';
import { RecordService } from './record.service';
import { environment } from 'environment/environment';
var CrpService = /** @class */ (function () {
    function CrpService(recordService, httpClient) {
        this.recordService = recordService;
        this.httpClient = httpClient;
        this.apiBase = environment.API_BASE;
    }
    /**
     * Use to filter which crps have not allowed to do screening.
     * @method getSearchableCrps
     */
    CrpService.prototype.getSearchableCrps = function (crps) {
        var _this = this;
        if (!crps) {
            return of([]);
        }
        return forkJoin(crps
            .filter(function (crp) { return crp.status === 'CURRENT'; })
            .map(function (crp) {
            if (crp.recordType === RecordType.CORPORATE) {
                return _this.recordService.isIncorporated(crp.record)
                    .pipe(map(function (isCorporated) {
                    return isCorporated ? crp : null;
                }));
            }
            return of(crp);
        })).pipe(map(function (finalCrps) {
            return finalCrps.filter(function (crp) { return crp !== null; });
        }));
    };
    /**
     * Create or update CRP info
     * @method createOrUpdateCrp
     */
    CrpService.prototype.createOrUpdateCrp = function (createOrUpdateCrpPayload) {
        var url = this.apiBase;
        if (createOrUpdateCrpPayload.crpData.individualRecord) {
            createOrUpdateCrpPayload.crpData.individualRecord = this.deleteNullContent(createOrUpdateCrpPayload.crpData.individualRecord);
        }
        else if (createOrUpdateCrpPayload.crpData.corporateRecord) {
            createOrUpdateCrpPayload.crpData.corporateRecord = this.deleteNullContent(createOrUpdateCrpPayload.crpData.corporateRecord);
        }
        if (createOrUpdateCrpPayload.mode === 'CREATE') {
            url += "/api/customers/" + createOrUpdateCrpPayload.customer.id + "/crps/";
            return this.httpClient.post(url, this.deleteNullContent(createOrUpdateCrpPayload.crpData));
        }
        else {
            url += "/api/crps/" + createOrUpdateCrpPayload.crpId + "/";
            return this.httpClient.patch(url, this.deleteNullContent(createOrUpdateCrpPayload.crpData));
        }
    };
    /**
     * Create or update CRP imported from Handshakes
     * @method createOrUpdateCrpFromHandshake
     */
    CrpService.prototype.createOrUpdateCrpFromHandshake = function (createOrUpdateCrpPayload) {
        var _this = this;
        var url = this.apiBase;
        var deletedRoles$;
        if (createOrUpdateCrpPayload.deletedRoles && createOrUpdateCrpPayload.deletedRoles.length > 0) {
            deletedRoles$ = zip.apply(void 0, createOrUpdateCrpPayload.deletedRoles.map(function (roleId) {
                return _this.httpClient.delete(_this.apiBase + "/api/roles/" + roleId + "/");
            }));
        }
        else {
            deletedRoles$ = of([]);
        }
        return deletedRoles$.pipe(concatMap(function (deletedRoles) {
            if (createOrUpdateCrpPayload.mode === 'CREATE') {
                url += "/api/customers/" + createOrUpdateCrpPayload.customer.id + "/crps/";
                return _this.httpClient.post(url, _this.deleteNullContent(_this.populateDefaultValue(createOrUpdateCrpPayload.crpData)));
            }
            else {
                url += "/api/crps/" + createOrUpdateCrpPayload.crpId + "/";
                return _this.httpClient.patch(url, _this.deleteNullContent(_this.populateDefaultValue(createOrUpdateCrpPayload.crpData)));
            }
        }));
    };
    /**
     * Get all roles of a CRP
     * @method getCrpRoles
     */
    CrpService.prototype.getCrpRoles = function (getCrpRolesRequestBody) {
        var params = new HttpParams();
        var url = this.apiBase + "/api/crps/" + getCrpRolesRequestBody.crpId + "/roles/";
        if (getCrpRolesRequestBody) {
            var reqBodyEntries = Object.entries(getCrpRolesRequestBody);
            reqBodyEntries.forEach(function (_a) {
                var key = _a[0], value = _a[1];
                if (value !== undefined) {
                    params = params.append(key, value);
                }
            });
        }
        return this.httpClient.get(url, { params: params });
    };
    /**
     * Add a CRP role
     * @method addCrpRole
     */
    CrpService.prototype.addCrpRole = function (crpRole) {
        var url = this.apiBase + "/api/crps/" + crpRole.crp + "/roles/";
        return this.httpClient.post(url, crpRole);
    };
    /**
     * Delete a CRP
     * @method deleteCrp
     */
    CrpService.prototype.deleteCrp = function (crpId) {
        return this.httpClient.delete(this.apiBase + "/api/crps/" + crpId + "/");
    };
    /**
     * Delete a CRP role
     * @method deleteRole
     */
    CrpService.prototype.deleteRole = function (roleId) {
        return this.httpClient.delete(this.apiBase + "/api/roles/" + roleId + "/");
    };
    /**
     * Update a CRP role
     * @method updateCrpRole
     */
    CrpService.prototype.updateCrpRole = function (crpRole) {
        return this.httpClient.put(this.apiBase + "/api/roles/" + crpRole.id + "/", crpRole);
    };
    /**
     * Remove null content in payload
     * @method deleteNullContent
     */
    CrpService.prototype.deleteNullContent = function (object) {
        for (var i in object) {
            if (object[i] === null &&
                !~['idExpiryDate', 'idIssueDate', 'dateOfBirth', 'incorporationDate', 'incorporationExpiryDate'].indexOf(i)) {
                object[i] = '';
            }
        }
        return object;
    };
    /**
     * Set to default value if the value is empty, for now it's only for Handshakes
     * @method populateDefaultValue
     */
    CrpService.prototype.populateDefaultValue = function (crpData) {
        crpData.status = 'CURRENT';
        if (crpData.individualRecord) {
            crpData.individualRecord.gender = crpData.individualRecord.gender || 'UNKNOWN';
            crpData.individualRecord.nationality = crpData.individualRecord.nationality || 'UNKNOWN';
            crpData.individualRecord.countryOfResidence = crpData.individualRecord.countryOfResidence || 'UNKNOWN';
            crpData.individualRecord.industry = crpData.individualRecord.industry || 'UNKNOWN - UNKNOWN';
            crpData.individualRecord.occupation = crpData.individualRecord.occupation || 'UNKNOWN - UNKNOWN';
            crpData.individualRecord.onboardingMode = crpData.individualRecord.onboardingMode || 'UNKNOWN';
            crpData.individualRecord.productServiceComplexity = crpData.individualRecord.productServiceComplexity || 'UNKNOWN';
            crpData.individualRecord.paymentModes = crpData.individualRecord.paymentModes || ['UNKNOWN'];
        }
        if (crpData.corporateRecord) {
            crpData.corporateRecord.isIncorporated = crpData.corporateRecord.isIncorporated || false;
            crpData.corporateRecord.entityType = crpData.corporateRecord.entityType || 'UNKNOWN';
            crpData.corporateRecord.countryOfIncorporation = crpData.corporateRecord.countryOfIncorporation || 'UNKNOWN';
            crpData.corporateRecord.countryOfOperations = crpData.corporateRecord.countryOfOperations || 'UNKNOWN';
            crpData.corporateRecord.ownershipStructureLayers = crpData.corporateRecord.ownershipStructureLayers || 'UNKNOWN';
            crpData.corporateRecord.onboardingMode = crpData.corporateRecord.onboardingMode || 'UNKNOWN';
            crpData.corporateRecord.productServiceComplexity = crpData.corporateRecord.productServiceComplexity || 'UNKNOWN';
            crpData.corporateRecord.paymentModes = crpData.corporateRecord.paymentModes || ['UNKNOWN'];
        }
        return crpData;
    };
    return CrpService;
}());
export { CrpService };
